import type { Book } from "../../shared/models";
import { legacyBookToDesktopV2 } from "../../shared/legacyBookToDesktopV2";
import {
  applyRemoteChaptersOnLoad,
  replaceLocalWithRemoteBook,
  runBidirectionalSync,
  seedChapterSyncBases,
  setLastSyncAt,
  pushBookStructureOnly,
  summarizePushFailures,
  type DesktopV2SyncAdapter,
} from "../../shared/desktopV2DbSync";
import { cloudAuthDesktopV2 } from "./cloudAuthDesktopV2";

function bookIdOf(book: Book): string {
  return typeof book.id === "string" ? book.id.trim() : "";
}

/**
 * Chapters the user deleted, per book, awaiting the next push. The server only
 * deletes what it is explicitly told to delete — a chapter simply missing from a
 * payload is treated as "not included", never as "removed".
 */
const pendingChapterDeletes = new Map<string, Set<string>>();

export function noteChapterDeleted(bookId: string, chapterId: string): void {
  const id = bookId.trim();
  if (!id || !chapterId) return;
  const set = pendingChapterDeletes.get(id) ?? new Set<string>();
  set.add(chapterId);
  pendingChapterDeletes.set(id, set);
}

function takePendingChapterDeletes(bookId: string): string[] {
  const set = pendingChapterDeletes.get(bookId);
  if (!set || set.size === 0) return [];
  return [...set];
}

function clearPendingChapterDeletes(bookId: string, sent: string[]): void {
  const set = pendingChapterDeletes.get(bookId);
  if (!set) return;
  for (const id of sent) set.delete(id);
  if (set.size === 0) pendingChapterDeletes.delete(bookId);
}

function webCloudAdapter(localSeed: Book): DesktopV2SyncAdapter {
  return {
    bookExists: (id) => cloudAuthDesktopV2.bookExists(id),
    getBookUpdatedAt: (id) => cloudAuthDesktopV2.getBookUpdatedAt(id),
    getChapterTimestamps: (id) => cloudAuthDesktopV2.getChapterTimestamps(id),
    loadRemoteBook: async (id) => {
      const payload = await cloudAuthDesktopV2.loadBook(id);
      return legacyBookToDesktopV2(payload, localSeed);
    },
    pushBook: async (book) => {
      const id = bookIdOf(book);
      const deletedChapterIds = takePendingChapterDeletes(id);
      const result = await cloudAuthDesktopV2.syncBook(book, { deletedChapterIds });
      clearPendingChapterDeletes(id, deletedChapterIds);
      return result;
    },
  };
}

export type AutoSyncMode = "web" | "none";

export function resolveAutoSyncMode(): AutoSyncMode {
  if (cloudAuthDesktopV2.isSignedIn()) return "web";
  return "none";
}

/** Push the local book to the library without pulling remote chapters first. */
export async function pushLocalBookToCloud(localBook: Book): Promise<{
  book: Book;
  merged: boolean;
  seeded?: boolean;
  pushed: boolean;
  error?: string;
}> {
  const mode = resolveAutoSyncMode();
  const id = bookIdOf(localBook);
  if (!id) {
    return { book: localBook, merged: false, pushed: false, error: "Book has no id" };
  }

  if (mode !== "web") {
    return { book: localBook, merged: false, pushed: false };
  }

  try {
    const { book: seededBook, seeded } = seedChapterSyncBases(localBook);
    const adapter = webCloudAdapter(seededBook);
    // Structure only. Prose reaches the server through `bookChapterSync`.
    const { book: pushedBook, pushResult } = await pushBookStructureOnly(seededBook, adapter);
    const failed = pushResult.failedChapters ?? [];
    if (failed.length > 0) {
      console.error("[pushLocalBookToCloud] structure push rejected chapters", failed);
      return {
        book: pushedBook,
        merged: false,
        seeded,
        pushed: false,
        error: summarizePushFailures(failed),
      };
    }
    setLastSyncAt(id, new Date());
    return { book: pushedBook, merged: false, seeded, pushed: true };
  } catch (e) {
    return {
      book: localBook,
      merged: false,
      pushed: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/** Pull newer remote chapters, merge by `updatedAt`, push merged book. */
export async function autoSyncBook(
  localBook: Book,
  options?: { pushOnly?: boolean }
): Promise<{
  book: Book;
  merged: boolean;
  seeded?: boolean;
  pushed: boolean;
  error?: string;
}> {
  const mode = resolveAutoSyncMode();
  const id = bookIdOf(localBook);
  if (!id) {
    return { book: localBook, merged: false, pushed: false, error: "Book has no id" };
  }

  if (mode === "web") {
    if (options?.pushOnly) {
      return pushLocalBookToCloud(localBook);
    }
    return runBidirectionalSync(localBook, webCloudAdapter(localBook));
  }

  return { book: localBook, merged: false, pushed: false };
}

/**
 * Open / recover: pull the library copy and take newer server saves.
 * Never pushes — a stale local snapshot must not overwrite web.
 */
export async function pullNewestOnLoad(localBook: Book): Promise<{
  book: Book;
  merged: boolean;
  pushed: false;
  error?: string;
}> {
  const id = bookIdOf(localBook);
  if (!id) {
    return { book: localBook, merged: false, pushed: false, error: "Book has no id" };
  }
  if (resolveAutoSyncMode() !== "web") {
    return { book: localBook, merged: false, pushed: false };
  }
  try {
    const adapter = webCloudAdapter(localBook);
    const exists = await adapter.bookExists(id);
    if (!exists) return { book: localBook, merged: false, pushed: false };
    const remoteBook = await adapter.loadRemoteBook(id);
    if (!remoteBook) return { book: localBook, merged: false, pushed: false };
    const { book, didChange } = applyRemoteChaptersOnLoad(localBook, remoteBook);
    const seeded = seedChapterSyncBases(book);
    return { book: seeded.book, merged: didChange || seeded.seeded, pushed: false };
  } catch (e) {
    return {
      book: localBook,
      merged: false,
      pushed: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

function resolveAdapter(localBook: Book): DesktopV2SyncAdapter | null {
  if (resolveAutoSyncMode() === "web") return webCloudAdapter(localBook);
  return null;
}

/** Discard unsaved local chapter edits and replace with the library copy. */
export async function pullRemoteOverrideLocal(localBook: Book): Promise<{
  book: Book;
  error?: string;
}> {
  const id = bookIdOf(localBook);
  if (!id) {
    return { book: localBook, error: "Book has no id" };
  }
  const adapter = resolveAdapter(localBook);
  if (!adapter) {
    return { book: localBook, error: "Not signed in for cloud sync" };
  }

  try {
    const remoteBook = await adapter.loadRemoteBook(id);
    if (!remoteBook) {
      return { book: localBook, error: "This book is not in your library yet, or it could not be loaded" };
    }
    const replaced = replaceLocalWithRemoteBook(localBook, remoteBook);
    setLastSyncAt(id, new Date());
    return { book: replaced };
  } catch (e) {
    return {
      book: localBook,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}
